import { useState } from 'react';
import {
  Card,
  Button,
  Badge,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, type User } from './useIamUsers';
import { formatDate } from '../../shared/utils/format';

/* ── Helper ── */

/* ── Component ── */

export function UsersPage(): JSX.Element {
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data, isLoading, error } = useUsers();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  // ── Form state ──
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');

  const resetForm = (): void => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setEditingUser(null);
    setShowForm(false);
  };

  const openEdit = (user: User): void => {
    setEditingUser(user);
    setFormName(user.name);
    setFormEmail(user.email);
    setFormPassword('');
    setShowForm(true);
  };

  const handleSubmit = (): void => {
    if (!formName.trim() || !formEmail.trim()) {
      addToast('Preencha nome e email.', 'error');
      return;
    }

    if (editingUser) {
      updateMutation.mutate(
        { id: editingUser.id, name: formName, email: formEmail },
        {
          onSuccess: () => {
            addToast('Usuário atualizado com sucesso', 'success');
            resetForm();
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao atualizar', 'error');
          },
        },
      );
    } else {
      if (!formPassword.trim()) {
        addToast('Senha é obrigatória para novos usuários.', 'error');
        return;
      }
      createMutation.mutate(
        { name: formName, email: formEmail, password: formPassword },
        {
          onSuccess: () => {
            addToast('Usuário criado com sucesso', 'success');
            resetForm();
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao criar', 'error');
          },
        },
      );
    }
  };

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Usuário removido com sucesso', 'success');
        setConfirmDeleteId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao remover', 'error');
        setConfirmDeleteId(null);
      },
    });
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="iam-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Usuários</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Usuários, status e vínculos.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowForm(true); }}>
            + Novo Usuário
          </Button>
        </div>
      </div>

      {/* ── Form Modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => !isPending && resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
            </div>
            <div className="modal-body">
              <label className="field-label">Nome</label>
              <input className="field-input" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Nome completo" disabled={isPending} />

              <label className="field-label">Email</label>
              <input className="field-input" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="email@exemplo.com" disabled={isPending} />

              {!editingUser && (
                <>
                  <label className="field-label">Senha</label>
                  <input className="field-input" type="password" value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder="Mín. 8 caracteres" disabled={isPending} />
                </>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="ghost" onClick={resetForm} disabled={isPending}>Cancelar</Button>
              <Button variant="primary" loading={isPending} onClick={handleSubmit}>
                {editingUser ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── List ── */}
      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar usuários: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhum usuário encontrado.</p>
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>Criar primeiro usuário</Button>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((user) => (
                  <tr key={user.id}>
                    <td className="cell-name">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge variant={user.isActive ? 'success' : 'neutral'}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="action-group">
                        <Button size="sm" variant="outline" onClick={() => openEdit(user)}>Editar</Button>
                        {confirmDeleteId === user.id ? (
                          <div className="inline-confirm">
                            <span className="confirm-text">Remover?</span>
                            <Button size="sm" variant="danger" loading={deleteMutation.isPending} onClick={() => handleDelete(user.id)}>Sim</Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)} disabled={deleteMutation.isPending}>Não</Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="danger"
                            /* Lia `user.active`, que nao existe: `!undefined` e
                               `true`, e o botao ficava desativado para TODOS os
                               usuarios. */
                            disabled={!user.isActive}
                            onClick={() => setConfirmDeleteId(user.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .iam-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .cell-name { font-weight: 600; }
        .action-group { display: flex; gap: 0.375rem; flex-wrap: wrap; align-items: center; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: #ffffff; border-radius: 8px; width: 480px; max-width: 90vw; box-shadow: 0 8px 32px rgba(0,0,0,0.15); }
        .modal-header { padding: 1.25rem 1.5rem; border-bottom: 1px solid #e0e3e5; }
        .modal-header h2 { margin: 0; font-size: 1.125rem; color: #191c1e; }
        .modal-body { padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }
        .modal-footer { padding: 1rem 1.5rem; border-top: 1px solid #e0e3e5; display: flex; justify-content: flex-end; gap: 0.5rem; }
        .field-label { font-size: 12px; font-weight: 500; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}
        .field-input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e0e3e5; border-radius: 4px; font-size: 0.875rem; box-sizing: border-box; }
        .field-input:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 2px rgba(0, 32, 69, 0.15); }
      `}</style>
    </div>
  );
}
